import { Button, Tab, Tabs } from '@heroui/react';
import { Upload } from 'lucide-react';
import React, { FC, useState } from 'react';

import { StockPhoto, useGetAdAssets, useGetStockUsage } from '../../../../../queries';
import { GRADIENT_PRESETS } from '../../../config/palettes';
import { useAssetUpload, useSceneEditing } from '../../../hooks';
import { useAdsGeneratorStore } from '../../../model/store';
import { BackgroundKind } from '../../../model/types';
import { ColorField, PanelSection, SliderField } from '../../common';
import { AssetGrid, AssetThumb, AssetTile, EmptyPanelHint, OptionCard, OptionGrid } from '../Editor.styled';
import { StockImportModal } from '../StockImportModal';
import { StockSearchGrid } from '../StockSearchGrid';

export const BackgroundPanel: FC = () => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const setBackground = useAdsGeneratorStore((state) => state.setBackground);
  const { patchBackground } = useSceneEditing();
  const { inputRef, accept, isUploading, openPicker, handleFiles } = useAssetUpload();
  const [stockPhoto, setStockPhoto] = useState<StockPhoto | null>(null);
  const { data: stockUsage } = useGetStockUsage();
  const { data: library = [] } = useGetAdAssets();

  if (!scene) return null;

  const { background } = scene;

  return (
    <>
      <PanelSection title="Tło">
        <Tabs
          size="sm"
          radius="sm"
          fullWidth
          selectedKey={background.kind}
          onSelectionChange={(key) => setBackground({ kind: key as BackgroundKind })}
          aria-label="Rodzaj tła"
        >
          <Tab key="solid" title="Kolor" />
          <Tab key="gradient" title="Gradient" />
          <Tab key="image" title="Zdjęcie" />
        </Tabs>
      </PanelSection>

      {background.kind === 'solid' && (
        <PanelSection title="Kolor tła">
          <ColorField label="Wypełnienie" value={background.color} onChange={(color) => patchBackground({ color }, 'bg-color')} />
        </PanelSection>
      )}

      {background.kind === 'gradient' && (
        <PanelSection title="Gradient">
          <OptionGrid $columns={3}>
            {GRADIENT_PRESETS.map((preset) => (
              <OptionCard
                key={preset.label}
                type="button"
                title={preset.label}
                onClick={() => setBackground({
                  gradientFrom: preset.from,
                  gradientTo: preset.to,
                  gradientAngle: preset.angle,
                })}
              >
                <span
                  className="block h-8 w-full rounded"
                  style={{ background: `linear-gradient(135deg, ${preset.from}, ${preset.to})` }}
                />
                {preset.label}
              </OptionCard>
            ))}
          </OptionGrid>
          <ColorField label="Kolor początkowy" value={background.gradientFrom} onChange={(color) => patchBackground({ gradientFrom: color }, 'bg-from')} />
          <ColorField label="Kolor końcowy" value={background.gradientTo} onChange={(color) => patchBackground({ gradientTo: color }, 'bg-to')} />
          <SliderField
            label="Kąt"
            suffix="°"
            min={0}
            max={360}
            value={background.gradientAngle}
            onChange={(gradientAngle) => patchBackground({ gradientAngle }, 'bg-angle')}
          />
        </PanelSection>
      )}

      {background.kind === 'image' && (
        <PanelSection title="Zdjęcie w tle">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            hidden
            onChange={async (event) => {
              const uploaded = await handleFiles(event.target.files);
              if (uploaded[0]) setBackground({ imageSrc: uploaded[0].url });
            }}
          />
          <Button
            size="sm"
            radius="sm"
            variant="flat"
            color="primary"
            startContent={<Upload size={16} />}
            isLoading={isUploading}
            onPress={openPicker}
          >
            Wgraj zdjęcie tła
          </Button>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Moje obrazy</p>
            {library.length === 0 ? (
              <EmptyPanelHint>Biblioteka jest pusta. Wgraj plik albo weź zdjęcie z banku.</EmptyPanelHint>
            ) : (
              <AssetGrid>
                {library.map((asset) => (
                  <AssetTile key={asset.id}>
                    <AssetThumb
                      type="button"
                      title={asset.name || 'Ustaw jako tło'}
                      onClick={() => setBackground({ kind: 'image', imageSrc: asset.url })}
                    >
                      <img src={asset.url} alt="" crossOrigin="anonymous" />
                    </AssetThumb>
                  </AssetTile>
                ))}
              </AssetGrid>
            )}
          </div>

          <p className="text-xs text-gray-500 leading-relaxed">
            Albo wybierz zdjęcie z banku Magnific. Przeglądanie nic nie kosztuje, pulę zabiera dopiero pobranie.
          </p>
          <StockSearchGrid initialTerm="modern hotel interior" onPick={setStockPhoto} />

          <ColorField label="Przyciemnienie" value={background.overlayColor} onChange={(overlayColor) => patchBackground({ overlayColor }, 'bg-overlay-color')} />
          <SliderField
            label="Siła przyciemnienia"
            suffix="%"
            min={0}
            max={100}
            value={Math.round(background.overlayOpacity * 100)}
            onChange={(value) => patchBackground({ overlayOpacity: value / 100 }, 'bg-overlay')}
          />
          {stockPhoto && (
            <StockImportModal
              photo={stockPhoto}
              downloadsLeft={stockUsage?.left ?? 0}
              lockedUsage="background"
              onClose={() => setStockPhoto(null)}
            />
          )}
        </PanelSection>
      )}
    </>
  );
};
